import type { ValueEvent } from '@adornis/base/utilTypes.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-formfield-wrapper.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { ImageFile } from '@adornis/file-utils/db/images.js';
import { FormField } from '@adornis/formfield/form-field.js';
import '@chancenwerk/client/design/components/cr-card.js';
import '@chancenwerk/client/design/components/cr-loader.js';
import '@chancenwerk/client/design/components/fonts.js';
import { AdvertisingMaterial, LearningMaterial, SelectedSetProduct } from '@chancenwerk/db/LogistikProduct.js';
import { AdvertisingMaterialAPI } from '@chancenwerk/db/api/AdvertisingMaterialAPI.js';
import { LearningMaterialAPI } from '@chancenwerk/db/api/LearningMaterialAPI.js';
import '@chancenwerk/design/components/cr-flex.js';
import { g_getProductCategoryString } from '@chancenwerk/logistik/client/helper.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { combineLatest, map } from 'rxjs';

const learningMaterialSelectionSet: BaseQLSelectionSet<LearningMaterial> = {
  _id: 1,
  imageID: 1,
  title: 1,
  _class: 1,
};

const advertisingMaterialSelectionSet: BaseQLSelectionSet<AdvertisingMaterial> = {
  _id: 1,
  imageID: 1,
  title: 1,
  _class: 1,
};

type SupportedProducts = LearningMaterial | AdvertisingMaterial;

@customElement('logistik-component-product-set-item-selector')
export class LogistikComponentLearningMaterialSelector extends FormField<SelectedSetProduct[]> {
  @state() protected readonly _allProducts = new RXController(
    this,
    combineLatest([
      LearningMaterialAPI.subscribe()(learningMaterialSelectionSet),
      AdvertisingMaterialAPI.subscribe()(advertisingMaterialSelectionSet),
    ]),
    obs =>
      obs.pipe(
        map(([lms, ams]) =>
          [...lms, ...ams].sort((a, b) => a.title.toLowerCase().trim().localeCompare(b.title.toLowerCase().trim())),
        ),
      ),
  );

  @state() protected readonly _selectedProducts = new RXController(
    this,
    combineLatest([this._allProducts.observable, this.value.observable]),
    obs =>
      obs.pipe(
        map(([allProducts, selectedProducts]) =>
          selectedProducts ? allProducts.filter(lm => selectedProducts.some(a => a.productID === lm._id)) : [],
        ),
      ),
  );

  @state() protected readonly _unselectedProducts = new RXController(
    this,
    combineLatest([this._allProducts.observable, this.value.observable]),
    obs =>
      obs.pipe(
        map(([allProducts, selectedProducts]) =>
          selectedProducts
            ? allProducts.filter(lm => !selectedProducts.some(a => a.productID === lm._id))
            : allProducts,
        ),
      ),
  );

  protected override render() {
    if (
      this._allProducts.isLoading ||
      !this._allProducts.value ||
      this._unselectedProducts.isLoading ||
      this._unselectedProducts.value === undefined ||
      this._selectedProducts.isLoading ||
      this._selectedProducts.value === undefined
    )
      return html` <cr-loader> </cr-loader> `;
    return html`
      <cr-flex horizontal space="sm">
        ${this._dragDropColumn(
          'Vorhandene Produkte',
          id => this._onDroppedItemLeft(id),
          this._unselectedProducts,
          p => this._productItem(p),
          'Keine weiteren Produkte vorhanden',
        )}
        ${this._dragDropColumn(
          'Ausgewählte Produkte',
          id => this._onDroppedItemRight(id),
          this._selectedProducts,
          p => this._productItem(p, true),
          'Noch keine Produkte ausgewählt',
          this.error,
        )}
      </cr-flex>
    `;
  }

  private _dragDropColumn(
    title: string,
    onDropped: (id: string) => void,
    products: RXController<any, SupportedProducts[]>,
    productRenderer: (product: SupportedProducts) => Renderable,
    emptyText: string,
    errorText: string | undefined = undefined,
  ): Renderable {
    const isError = errorText && !products.value?.length;
    return html`
      <cr-flex
        flex
        end
        ${css({
          background: isError ? this.colors.tone.error.shade(50) : '#f2f2f2',
          borderRadius: `${this.spacing.sm} ${this.spacing.sm} 0 0`,
          border: `3px dashed ${isError ? this.colors.tone.error : '#e5e5e5'}`,
        })}
        padding="sm"
      >
        <cr-flex padding="md">
          <cr-text> ${title} </cr-text>
        </cr-flex>
        <x-drag-drop
          allow-from="cr-flex.draggable"
          child-selector="cr-flex.draggable"
          dropzone-accept-selector="cr-flex.draggable"
          @dropped=${(e: CustomEvent<HTMLElement>) => {
            if (e.detail.id) onDropped(e.detail.id);
          }}
          ${css({
            overflowX: 'hidden',
            overflowY: 'auto',
            height: '100%',
            width: '100%',
            maxHeight: '300px',
          })}
        >
          <cr-flex w100 h100>
            <cr-flex w100 h100 padding="xs">
              ${products.value?.length
                ? products.value?.map(p => productRenderer(p))
                : html` <cr-flex w100 h100 center crossaxis-center space-around>
                    <cr-h4 ${css({ color: errorText ? this.colors.tone.error : undefined })}>
                      ${errorText ? errorText : emptyText}
                    </cr-h4>
                  </cr-flex>`}
            </cr-flex>
          </cr-flex>
        </x-drag-drop>
      </cr-flex>
    `;
  }

  private _onDroppedItemLeft(id: string) {
    // Probably, an item on the left was picked up, then dropped again.
    if (!this.value.value) return;
    const sp = this.value.value.find(i => i.productID === id);
    if (!sp) return;
    // now we know that value is non-null and non-empty because it must include id
    // Item was dragged from right to left, de-selecting it.
    const index = this.value.value.indexOf(sp);
    this.value.value.splice(index, 1);
    this._setNewValue(this.value.value);
  }
  private _onDroppedItemRight(id: string) {
    if (!this.value.value) this.value.next([]);
    // We know the value is non-null, because we just set it.
    // Item is already on the right and was picked up, then dropped again.
    if (this.value.value!.find(a => a.productID === id)) return;
    // Item was dragged from left to right, selecting it.
    this._setNewValue([...this.value.value!, new SelectedSetProduct({ productID: id })]);
  }

  private _setNewValue(newValue: SelectedSetProduct[]) {
    this.value.next(newValue);
    this.dispatchEvent(
      new CustomEvent('value-picked', {
        detail: { value: this.value.value },
      }),
    );
  }

  private _productItem(product: SupportedProducts, showQuantity: boolean = false): Renderable {
    return html`
      <cr-flex ${css({ userSelect: 'none' })} class="draggable" w100 id=${product._id} key=${product._id} padding="xs">
        <cr-card ${css({ width: '100%', height: '100%', alignItems: 'center', flexDirection: 'row' })}>
          ${this._image(product.imageID)}
          <cr-flex padding="sm" h100>
            <cr-flex padding="xs">
              <cr-text bold> ${g_getProductCategoryString(product._class)} </cr-text>
            </cr-flex>
            <cr-flex padding="xs">
              <cr-text> ${product.title} </cr-text>
            </cr-flex>
          </cr-flex>
          ${showQuantity
            ? html`
                <cr-input
                  ${css({ width: '80px' })}
                  type="number"
                  min="1"
                  .value=${this.value.value?.find(a => a.productID === product._id)?.quantity}
                  @value-changed=${(e: ValueEvent<number>) => {
                    if (!this.value.value) return;
                    const index = this.value.value.findIndex(a => a.productID === product._id);
                    if (index === -1) return;
                    this.value.value[index]!.quantity = e.detail.value;
                    this._setNewValue(this.value.value);
                  }}
                >
                </cr-input>
              `
            : nothing}
        </cr-card>
      </cr-flex>
    `;
  }

  private _image(imageID: string): Renderable {
    const N = 70;
    const size = `${N}x${N}`;
    const width = `${N}px`;
    const height = `${N}px`;
    return html`
      <cr-flex flex-none>
        <img src=${ImageFile.getServeLink(imageID, { size })} ${css({ width, height })} />
      </cr-flex>
    `;
  }
}
